import { Link, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { BrandLockup } from '../components/Brand.tsx';
import { Icon } from '../components/Icon.tsx';
import { Reveal } from '../components/motion.tsx';
import { Button, Callout, Dialog, Empty, Field, PageHead, Panel, Skeleton, useToast } from '../components/ui.tsx';
import { ApiError, get, post } from '../lib/api.ts';
import { date, dateRange, idr } from '../lib/format.ts';
import { useMe } from '../lib/session.ts';

interface Line {
  id: string;
  name: string;
  code: string;
  kind: 'earning' | 'deduction';
  amount: string;
  calculated: boolean;
  shown: boolean;
  formula: string | null;
  note: string | null;
  source: string;
}
interface Slip {
  id: string;
  employee: { name: string; employeeNo: string | null };
  run: { periodFrom: string; periodTo: string; status: string; transferDate: string | null };
  basicSalary: string;
  totalEarnings: string;
  totalDeductions: string;
  netPay: string;
  splitBasic: string | null;
  splitCompetence: string | null;
  isSecondCompany: boolean;
  /** Hearth's own working, or (method 'legacy') the old system's, mapped by the API; parts may be missing. */
  incomeTax: null | { ptkp?: { code: string; annual: string }; totalIncome?: string; positionAllowance?: string; pkpMonthly?: string; pkpAnnual?: string; withheld: string; debt?: string; breakdown?: { bracket: string; rate: string; base: string; tax: string }[]; method?: string };
  inputs: { metrics?: { totalWorkDays: number; totalWorkingDays: number; daysAbsent: number; minLate: number; minEarlyOut: number; minOvertime: number; minShortLeave: number } } | null;
  warnings?: string[];
  lines: Line[];
}

export function Payslip() {
  const { id } = useParams({ strict: false }) as { id: string };
  const me = useMe().data!;
  const q = useQuery({ queryKey: ['payslip', id], queryFn: () => get<Slip>(`/api/payroll/payslips/${id}`) });
  const [showTax, setShowTax] = useState(false);
  const [showFormulas, setShowFormulas] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const corrections = useQuery({ queryKey: ['payslip-corrections', id], enabled: me.access.payroll, queryFn: () => get<{ id: string; amount: string; notes: string | null; paidInRunId: string | null; createdAt: string }[]>(`/api/payroll/payslips/${id}/corrections`) });
  const s = q.data;
  if (q.isLoading) return <Skeleton h={600} />;
  if (!s) return <Callout tone="bad">{(q.error as Error)?.message ?? 'Payslip not available.'}</Callout>;
  const earnings = s.lines.filter((l) => l.kind === 'earning' && l.calculated && l.amount !== '0.00');
  const deductions = s.lines.filter((l) => l.kind === 'deduction' && l.calculated && l.amount !== '0.00');
  const info = s.lines.filter((l) => !l.calculated && l.shown);
  const m = s.inputs?.metrics;
  return (
    <div>
      <div className="row row--between no-print" style={{ margin: 'var(--s-6) 0' }}>
        <Link to={me.access.payroll ? '/payroll' : '/payslips'} className="link faint">
          ← {me.access.payroll ? 'Payroll' : 'My payslips'}
        </Link>
        <div className="row">
          {me.access.payroll ? (
            <Button size="sm" variant="ghost" icon="formula" onClick={() => setShowFormulas((v) => !v)}>
              {showFormulas ? 'Hide' : 'Show'} formulas
            </Button>
          ) : null}
          {me.access.payrollRun && (s.run.status === 'approved' || s.run.status === 'paid') ? (
            <Button size="sm" variant="ghost" icon="plus" onClick={() => setCorrecting(true)}>
              Correction
            </Button>
          ) : null}
          <Button size="sm" icon="download" onClick={() => window.print()}>
            Print / PDF
          </Button>
        </div>
      </div>
      {corrections.data?.length ? (
        <div className="no-print" style={{ marginBottom: 'var(--s-5)' }}>
          <Callout tone="info" title={`${corrections.data.length} correction${corrections.data.length === 1 ? '' : 's'} to this payslip`}>
            {corrections.data.map((k) => `${Number(k.amount) > 0 ? '+' : ''}${idr(k.amount)} — ${k.notes ?? ''} (${k.paidInRunId ? 'paid in a later run' : 'pays in the next run'})`).join(' · ')}
          </Callout>
        </div>
      ) : null}
      {correcting ? <CorrectionDialog id={id} onClose={() => setCorrecting(false)} /> : null}
      <Reveal>
        <Reveal.Item className="payslip-doc">
          <header className="row row--between" style={{ alignItems: 'start' }}>
            <div className="stack" style={{ ['--gap' as string]: '10px' }}>
              <BrandLockup className="payslip-doc__lockup" />
              <div className="eyebrow">{s.isSecondCompany ? 'Second company payslip' : 'Payslip'}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="eyebrow">Period</div>
              <div className="display" style={{ fontSize: '1.4rem' }}>
                {dateRange(s.run.periodFrom, s.run.periodTo)}
              </div>
              {s.run.transferDate ? <div className="faint" style={{ fontSize: 'var(--step--2)' }}>Paid {date(s.run.transferDate)}</div> : null}
            </div>
          </header>
          <hr className="rule" style={{ margin: 'var(--s-6) 0' }} />
          <div className="row row--between row--wrap">
            <div>
              <div className="eyebrow">Employee</div>
              <div className="display" style={{ fontSize: 'var(--step-3)' }}>
                {s.employee.name}
              </div>
              <div className="mono faint">{s.employee.employeeNo}</div>
            </div>
            <dl className="row row--wrap" style={{ ['--gap' as string]: '28px', fontSize: 'var(--step--1)' }}>
              <div>
                <dt className="eyebrow">Work days</dt>
                <dd className="num">{m ? m.totalWorkingDays : '—'}</dd>
              </div>
              <div>
                <dt className="eyebrow">Absent</dt>
                <dd className="num">{m ? m.daysAbsent : '—'}</dd>
              </div>
              <div>
                <dt className="eyebrow">Late</dt>
                <dd className="num">{m ? `${m.minLate} min` : '—'}</dd>
              </div>
              <div>
                <dt className="eyebrow">Overtime</dt>
                <dd className="num">{m ? `${Math.round(m.minOvertime / 6) / 10} h` : '—'}</dd>
              </div>
            </dl>
          </div>

          <div className="payslip-lines" style={{ marginTop: 'var(--s-6)' }}>
            <div>
              <div className="eyebrow" style={{ marginBottom: 6 }}>
                Earnings
              </div>
              <div className="payslip-line">
                <span>Basic salary</span>
                <span className="num">{idr(s.basicSalary)}</span>
              </div>
              {earnings.map((l) => (
                <div key={l.id} className="payslip-line">
                  <span>
                    {l.name}
                    {showFormulas && l.formula ? <div className="code-inline" style={{ marginTop: 4, display: 'inline-block' }}>{l.formula}</div> : null}
                  </span>
                  <span className="num">{idr(l.amount)}</span>
                </div>
              ))}
            </div>
            <div>
              <div className="eyebrow" style={{ marginBottom: 6 }}>
                Deductions
              </div>
              {deductions.map((l) => (
                <div key={l.id} className="payslip-line">
                  <span>
                    {l.name}
                    {l.note ? <span className="faint"> · {l.note}</span> : null}
                    {showFormulas && l.formula ? <div className="code-inline" style={{ marginTop: 4, display: 'inline-block' }}>{l.formula}</div> : null}
                  </span>
                  <span className="num">−{idr(l.amount).replace('Rp ', 'Rp ')}</span>
                </div>
              ))}
              {!deductions.length ? <p className="faint">No deductions.</p> : null}
            </div>
          </div>

          <div className="payslip-net">
            <div>
              <div className="eyebrow">Take-home pay</div>
              {s.splitBasic && s.splitCompetence && Number(s.splitCompetence) > 0 ? (
                <div className="faint" style={{ fontSize: 'var(--step--2)', marginTop: 6 }}>
                  Transferred as {idr(s.splitBasic)} salary + {idr(s.splitCompetence)} competence grant
                </div>
              ) : null}
            </div>
            <span className="display num" style={{ color: 'var(--accent-text)' }}>
              {idr(s.netPay)}
            </span>
          </div>

          {info.length ? (
            <div style={{ marginTop: 'var(--s-5)' }}>
              <div className="eyebrow" style={{ marginBottom: 6 }}>
                Paid separately / for your information
              </div>
              {info.map((l) => (
                <div key={l.id} className="payslip-line">
                  <span className="muted">{l.name}</span>
                  <span className="num muted">{idr(l.amount)}</span>
                </div>
              ))}
            </div>
          ) : null}

          {/* Payroll staff only until management can explain "Gross for tax" to employees (the API holds it back too). */}
          {s.incomeTax && me.access.payroll ? (
            <div style={{ marginTop: 'var(--s-6)' }}>
              <button className="link" style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer', fontSize: 'var(--step--1)' }} onClick={() => setShowTax((v) => !v)} aria-expanded={showTax}>
                <Icon name={showTax ? 'chevronDown' : 'chevronRight'} width={14} height={14} style={{ display: 'inline', verticalAlign: '-2px' }} /> How your PPh 21 was worked out
              </button>
              {showTax ? (
                <dl className="dl" style={{ marginTop: 12 }}>
                  {s.incomeTax.method === 'legacy' ? (
                    <>
                      <dt>Worked out by</dt>
                      <dd className="faint">The old payroll system, which applied the brackets to the monthly amount</dd>
                    </>
                  ) : null}
                  {s.incomeTax.totalIncome !== undefined ? (
                    <>
                      <dt>Gross for tax</dt>
                      <dd className="num">{idr(s.incomeTax.totalIncome)}</dd>
                    </>
                  ) : null}
                  {s.incomeTax.positionAllowance !== undefined ? (
                    <>
                      <dt>Position allowance (5%, max Rp 500.000)</dt>
                      <dd className="num">−{idr(s.incomeTax.positionAllowance)}</dd>
                    </>
                  ) : null}
                  {s.incomeTax.ptkp ? (
                    <>
                      <dt>PTKP {s.incomeTax.ptkp.code} ÷ 12</dt>
                      <dd className="num">−{idr(Number(s.incomeTax.ptkp.annual) / 12)}</dd>
                    </>
                  ) : null}
                  {s.incomeTax.pkpMonthly !== undefined ? (
                    <>
                      <dt>Taxable (PKP) per month</dt>
                      <dd className="num">{idr(s.incomeTax.pkpMonthly)}</dd>
                    </>
                  ) : null}
                  {(s.incomeTax.breakdown ?? []).map((b) => (
                    <Fragment key={b.bracket}>
                      <dt>
                        {b.bracket} at {(Number(b.rate) * 100).toFixed(0)}%
                      </dt>
                      <dd className="num">
                        {idr(b.base)} → {idr(b.tax)}
                      </dd>
                    </Fragment>
                  ))}
                  {Number(s.incomeTax.debt ?? 0) ? (
                    <>
                      <dt>Tax debt</dt>
                      <dd className="num">{idr(s.incomeTax.debt!)}</dd>
                    </>
                  ) : null}
                  <dt>Withheld this month</dt>
                  <dd className="num" style={{ fontWeight: 600 }}>
                    {idr(s.incomeTax.withheld)}
                  </dd>
                </dl>
              ) : null}
            </div>
          ) : null}
          {me.access.payroll && s.warnings?.length ? (
            <div style={{ marginTop: 'var(--s-5)' }} className="no-print">
              <Callout title="Engine notes.">{s.warnings.join(' ')}</Callout>
            </div>
          ) : null}
        </Reveal.Item>
      </Reveal>
    </div>
  );
}

export function MyPayslips() {
  const q = useQuery({ queryKey: ['my-payslips'], queryFn: () => get<{ id: string; netPay: string; totalEarnings: string; totalDeductions: string; basicSalary: string; periodFrom: string; periodTo: string; transferDate: string | null }[]>('/api/payroll/me/payslips') });
  const thr = useQuery({ queryKey: ['my-thr'], queryFn: () => get<{ id: string; feast: string; year: number; transferDate: string; received: string; workedMonths: number }[]>('/api/payroll/me/thr') });
  return (
    <div>
      <PageHead
        eyebrow="My payslips"
        title={
          <>
            What you’ve <em>been paid.</em>
          </>
        }
        lede="Payslips appear once a payroll run is approved. Each one shows exactly how it was calculated."
      />
      <Panel>
        {q.isLoading ? (
          <Skeleton h={160} />
        ) : !q.data?.length ? (
          <Empty title="No payslips yet." />
        ) : (
          <ul className="list-plain">
            {q.data.map((s) => (
              <li key={s.id}>
                <Link to="/payslips/$id" params={{ id: s.id }} className="run-card" style={{ gridTemplateColumns: '1fr auto auto' }}>
                  <div>
                    <div className="run-card__period">{dateRange(s.periodFrom, s.periodTo)}</div>
                    <div className="eyebrow" style={{ marginTop: 6 }}>
                      {s.transferDate ? `Paid ${date(s.transferDate)}` : ''}
                    </div>
                  </div>
                  <span className="display num" style={{ fontSize: '1.6rem' }}>
                    {idr(s.netPay)}
                  </span>
                  <Icon name="chevronRight" width={18} height={18} className="faint" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {thr.data?.length ? (
        <Panel eyebrow="Holiday allowance" title="THR" className="panel--spaced">
          <ul className="list-plain">
            {thr.data.map((t) => (
              <li key={t.id} className="run-card" style={{ gridTemplateColumns: '1fr auto' }}>
                <div>
                  <div className="run-card__period">{t.feast} {t.year}</div>
                  <div className="eyebrow" style={{ marginTop: 6 }}>Paid {date(t.transferDate)} · {t.workedMonths} of 12 months</div>
                </div>
                <span className="display num" style={{ fontSize: '1.6rem' }}>{idr(t.received)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </div>
  );
}

function CorrectionDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState({ amount: '', notes: '' });
  const m = useMutation({
    mutationFn: () => post(`/api/payroll/payslips/${id}/corrections`, { amount: f.amount.replace(/[^\d-]/g, ''), notes: f.notes }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payslip-corrections', id] });
      toast({ title: 'Correction recorded', body: 'It is paid with the next payroll run.' });
      onClose();
    },
  });
  return (
    <Dialog open onClose={onClose} eyebrow="After approval" title="Correct this payslip" footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>Record correction</Button></>}>
      <div className="stack">
        <p className="muted" style={{ fontSize: 'var(--step--1)' }}>Approved payslips don’t change. The difference is paid, or recovered, in the next run as a separate line, so both payslips stay true.</p>
        <Field label="Amount (Rp)" hint="Negative to recover an overpayment, e.g. -50000">{(p) => <input {...p} className="input input--mono" inputMode="numeric" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />}</Field>
        <Field label="What happened">{(p) => <textarea {...p} className="input" rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />}</Field>
        {m.error ? <Callout tone="bad">{m.error instanceof ApiError ? m.error.message : 'Failed'}</Callout> : null}
      </div>
    </Dialog>
  );
}
